import extractQueryParamFromPageLocation from '@src/quince-site-common/src/utils/extractQueryParamFromPageLocation'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getCookieStorage } from '@src/utils/enhancedCookie'

/**
 * AppLovin tracking constants
 * Based on AppLovin documentation
 */
export const APPLOVIN_TRACKING_CONSTANTS = {
  CLICK_ID_URL_PARAM: 'aleid',
  CLICK_ID_COOKIE: '_aleid',
  AXEID_COOKIE: '_axeid',
  AXWRT_COOKIE: '_axwrt',
  ALART_URL_PARAM: 'alart',
  ALART_COOKIE: 'alart',
  COOKIE_EXPIRY_MINUTES: 365 * 24 * 60,
} as const

/**
 * Extracts query parameter from referrer URL
 */
const extractQueryParamFromReferrer = (key: string): string => {
  try {
    if (typeof window === 'undefined' || !window.document.referrer) {
      return ''
    }

    const url = new URL(window.document.referrer)
    return url.searchParams.get(key) ?? ''
  } catch (error) {
    return ''
  }
}

/**
 * Sets the _aleid cookie when aleid URL parameter is present
 * AppLovin recommends setting this cookie with one-year expiration
 */
const setAleidCookie = (aleid: string): void => {
  if (aleid && typeof window !== 'undefined') {
    const cookieStorage = getCookieStorage()
    const cookieOptions = {
      isGlobal: true,
      expires: new Date(Date.now() + APPLOVIN_TRACKING_CONSTANTS.COOKIE_EXPIRY_MINUTES * 60 * 1000),
      secure: true,
    }
    cookieStorage.setCookieLocalized(APPLOVIN_TRACKING_CONSTANTS.CLICK_ID_COOKIE, aleid, cookieOptions)
    cookieStorage.setCookieLocalized(APPLOVIN_TRACKING_CONSTANTS.AXEID_COOKIE, aleid, cookieOptions)
  }
}

/**
 * Extracts AppLovin Click ID (aleid) with proper priority
 * Priority: URL Parameter > Cookie > Referrer
 *
 * AppLovin flow: URL parameter â†’ Cookie â†’ Our extraction
 */
export const getAppLovinClickId = (): string => {
  // 1. First try URL parameter (AppLovin adds aleid to destination URL)
  const urlClickId: string = extractQueryParamFromPageLocation(APPLOVIN_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  if (urlClickId) {
    // Set the cookie when we find the URL parameter
    setAleidCookie(urlClickId)
    return urlClickId
  }

  // 2. Try primary AppLovin cookie
  const clickId = getCookie(APPLOVIN_TRACKING_CONSTANTS.CLICK_ID_COOKIE)
  if (clickId) {
    return clickId
  }

  // 3. Fallback to referrer (if available)
  const referrerClickId: string = extractQueryParamFromReferrer(APPLOVIN_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  if (referrerClickId) {
    // Set the cookie when we find the referrer parameter
    setAleidCookie(referrerClickId)
    return referrerClickId
  }

  return ''
}

/**
 * Extracts AppLovin AXWRT from cookie (if AXON Pixel is installed)
 */
export const getAppLovinAxwrt = (): string => {
  const axwrt = getCookie(APPLOVIN_TRACKING_CONSTANTS.AXWRT_COOKIE)
  return axwrt ?? ''
}

/**
 * Extracts AppLovin ALART from URL parameter (for App users)
 */
export const getAppLovinAlart = (): string => {
  const alart: string = extractQueryParamFromPageLocation(APPLOVIN_TRACKING_CONSTANTS.ALART_URL_PARAM)
  if (alart && typeof window !== 'undefined') {
    const cookieStorage = getCookieStorage()
    cookieStorage.setCookieLocalized(APPLOVIN_TRACKING_CONSTANTS.ALART_COOKIE, alart, {
      isGlobal: true,
      expires: new Date(Date.now() + APPLOVIN_TRACKING_CONSTANTS.COOKIE_EXPIRY_MINUTES * 60 * 1000),
      secure: true,
    })
  }
  if (alart) return alart

  return getCookie(APPLOVIN_TRACKING_CONSTANTS.ALART_COOKIE) ?? ''
}

/**
 * Gets all AppLovin tracking data as an object
 */
export const getAppLovinTrackingData = (): {
  aleid: string
  axwrt: string
  alart: string
} => {
  return {
    aleid: getAppLovinClickId(),
    axwrt: getAppLovinAxwrt(),
    alart: getAppLovinAlart(),
  }
}
